import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { LocationsPage } from './pages/LocationsPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { MeshesPage } from './pages/MeshesPage';
import { ProductsPage } from './pages/ProductsPage';
import { BeaconAssignmentsPage } from './pages/BeaconAssignmentsPage';
import BeaconConcernsPage from './pages/BeaconConcernsPage';
import { LocationAssignmentsPage } from './pages/LocationAssignmentsPage';
import { EdgeAccountDigestsPage } from './pages/EdgeAccountDigestsPage';
import EdgeReviewsPage from './pages/EdgeReviewsPage';
import EdgeReviewLocationPage from './pages/EdgeReviewLocationPage';
import { SyncGeneratorPage } from './pages/SyncGeneratorPage';
import { ProductEquivalenciesPage } from './pages/ProductEquivalenciesPage';
import { ManufacturersPage } from './pages/ManufacturersPage';
import { ProductEquivalencyCategoriesPage } from './pages/ProductEquivalencyCategoriesPage';
import { LocationEquivalenciesPage } from './pages/LocationEquivalenciesPage';
import { CacheManagementPage } from './pages/CacheManagementPage';
import { DevicesPage } from './pages/DevicesPage';
import { DeviceDetailPage } from './pages/DeviceDetailPage';
import { LocationDashboardPage } from './pages/LocationDashboardPage';
import { FleetDashboardPage } from './pages/FleetDashboardPage';
import { InternalOpsDashboardPage } from './pages/InternalOpsDashboardPage';
import { GoLivesPage } from './pages/onboarding/GoLivesPage';
import { GoLiveDetailPage } from './pages/onboarding/GoLiveDetailPage';
import { OnboardingUsersPage } from './pages/onboarding/OnboardingUsersPage';
import { TagSlipsPage } from './pages/onboarding/TagSlipsPage';
import { OnboardingGuidePage } from './pages/onboarding/OnboardingGuidePage';
import { createTheme } from '@mui/material/styles';

const queryClient = new QueryClient();

const theme = createTheme({
  palette: {
    primary: {
      main: '#1b5e20',
    },
    secondary: {
      main: '#66bb6a',
    },
  },
});

// Get Google OAuth Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  if (!GOOGLE_CLIENT_ID) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Configuration Error</h2>
          <p>Google OAuth Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable.</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Router basename="/">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<LocationsPage />} />
                          <Route path="/fleet" element={<FleetDashboardPage />} />
                          <Route path="/internal-ops" element={<InternalOpsDashboardPage />} />
                          <Route path="/organizations" element={<OrganizationsPage />} />
                          <Route path="/meshes" element={<MeshesPage />} />
                          <Route path="/locations" element={<LocationsPage />} />
                          <Route path="/locations/:id/dashboard" element={<LocationDashboardPage />} />
                          <Route path="/devices" element={<DevicesPage />} />
                          <Route path="/devices/:id" element={<DeviceDetailPage />} />
                          <Route path="/products" element={<ProductsPage />} />
                          <Route path="/beacon_assignments" element={<BeaconAssignmentsPage />} />
                          <Route path="/beacon_concerns" element={<BeaconConcernsPage />} />
                          <Route path="/location_assignments" element={<LocationAssignmentsPage />} />
                          <Route path="/manufacturers" element={<ManufacturersPage />} />
                          <Route path="/product_equivalencies" element={<ProductEquivalenciesPage />} />
                          <Route path="/product_equivalency_categories" element={<ProductEquivalencyCategoriesPage />} />
                          <Route path="/location_equivalencies" element={<LocationEquivalenciesPage />} />
                          <Route path="/edge_account_digests" element={<EdgeAccountDigestsPage />} />
                          <Route path="/onboarding/go_lives" element={<GoLivesPage />} />
                          <Route path="/onboarding/go_lives/:id" element={<GoLiveDetailPage />} />
                          <Route path="/onboarding/users" element={<OnboardingUsersPage />} />
                          <Route path="/onboarding/tag_slips" element={<TagSlipsPage />} />
                          <Route path="/onboarding/guide" element={<OnboardingGuidePage />} />
                          <Route path="/outcomes" element={<EdgeReviewsPage />} />
                          <Route path="/outcomes/locations/:id" element={<EdgeReviewLocationPage />} />
                          <Route path="/sync_generator" element={<SyncGeneratorPage />} />
                          <Route path="/cache_management" element={<CacheManagementPage />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
